import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import sumBy from 'lodash/sumBy';

import {
  OptimizationWorkloadContainerMetrics,
  OptimizationWorkloadPod,
} from '@cast/types';

import { AppliedRecommendation } from '../../../workload-optimization';

type Timestamped = { timestamp: string };

/**
 * Earliest first / latest last across series so CPU, Memory, and HPA charts
 * share one time grid (Recharts syncId aligns tooltips by index).
 *
 * Uses data bounds (not the picker range) so fillGaps cannot walk from stale
 * fixture/API timestamps up to "now" and hang the page.
 */
export const getSharedMetricsDateRange = (
  seriesList: Timestamped[][]
): { from: string; to: string } | undefined => {
  let earliest: dayjs.Dayjs | undefined;
  let latest: dayjs.Dayjs | undefined;

  for (const series of seriesList) {
    if (series.length < 1) {
      continue;
    }
    const first = dayjs.utc(series[0].timestamp);
    const last = dayjs.utc(series[series.length - 1].timestamp);
    if (!earliest || first.isBefore(earliest)) {
      earliest = first;
    }
    if (!latest || last.isAfter(latest)) {
      latest = last;
    }
  }

  if (!earliest || !latest) {
    return undefined;
  }

  return { from: earliest.toISOString(), to: latest.toISOString() };
};

export const getWorkloadLatestAppliedRecommendation = (
  pods: OptimizationWorkloadPod[]
): AppliedRecommendation | undefined => {
  // workload has applied recommendation only when
  // - all pods have the latest recommendation
  // - and there are no resource mismatches (nothing else modifying containers)
  if (
    isEmpty(pods) ||
    !pods.every(
      (it) =>
        it.isLatestRecommendation &&
        it.recommendation &&
        !it.recommendation.resourcesMismatched
    )
  ) {
    return undefined;
  }

  const sortedPods = sortBy(pods, (pod) => pod.recommendation?.appliedAt);
  const recentUpdatedPod = sortedPods[sortedPods.length - 1];

  const cpu = sumBy(
    recentUpdatedPod.containers,
    (container) => container.resources?.requests?.cpuCores ?? 0
  );

  const memory = sumBy(
    recentUpdatedPod.containers,
    (container) => container.resources?.requests?.memoryGib ?? 0
  );

  return {
    appliedAt: recentUpdatedPod.recommendation!.appliedAt,
    cpu,
    memory,
  };
};

/**
 * Filler inserted by `fillGaps` for `cpuCores` and `memoryGib` time series.
 *
 * `rec` and `req` are piecewise-constant; missing samples use `null` (not `0`)
 * so the chart breaks the line at the gap. Other keys stay 0-filled.
 */
export const RESOURCE_METRICS_GAP_FILLER = {
  min: 0,
  p25: 0,
  p50: 0,
  p75: 0,
  max: 0,
  rec: null,
  req: null,
  avg: 0,
} as unknown as OptimizationWorkloadContainerMetrics;
